import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { loginWithEmail, registerWithEmail, forgotPassword, fetchMe, applyReferralCode } from "@/lib/api";
import { toast } from "sonner";
import { X, Mail, Lock, User as UserIcon, ArrowLeft } from "lucide-react";
import { getStoredRef, clearStoredRef } from "@/lib/referral";

function formatErr(detail) {
  if (!detail) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

/**
 * LoginModal — three-tab auth modal.
 * Tabs: "sign in", "sign up", "forgot password".
 * Also renders Google + (disabled) Apple + Facebook buttons.
 */
export default function LoginModal({ open, onClose, defaultTab = "signin" }) {
  const { startGoogleLogin, setUser } = useAuth();
  const [tab, setTab] = useState(defaultTab);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  if (!open) return null;

  const closeAndReset = () => {
    setEmail(""); setPassword(""); setName(""); setError("");
    setTab("signin");
    onClose();
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const r = await loginWithEmail(email, password);
      setUser({ ...r.user });
      // Re-hydrate favIds
      try { await fetchMe(); } catch {}
      toast.success(`Welcome back, ${r.user.name || r.user.email}`);
      closeAndReset();
    } catch (err) {
      setError(formatErr(err?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const ref = getStoredRef();
      const r = await registerWithEmail(email, password, name, ref);
      if (ref) clearStoredRef();
      setUser({ ...r.user });
      toast.success(ref ? "Account created — bonus deal pack unlocked 🎁" : "Account created ✨");
      closeAndReset();
    } catch (err) {
      setError(formatErr(err?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      await forgotPassword(email);
      toast.success("Check your inbox for a reset link 📬");
      setTab("signin");
      setError("");
    } catch (err) {
      setError(formatErr(err?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const notReady = (label) => () => {
    toast(`${label} sign-in coming soon`, {
      description: `Ask the site owner to add ${label} developer credentials.`,
    });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onClick={closeAndReset}
      data-testid="login-modal-overlay"
    >
      <div
        className="w-full max-w-md bg-[#fff8ef] border-2 border-[#2d2a32] rounded-2xl hard-shadow p-6 relative"
        onClick={(e) => e.stopPropagation()}
        data-testid="login-modal"
      >
        <button
          onClick={closeAndReset}
          data-testid="login-modal-close"
          className="absolute top-3 right-3 w-8 h-8 rounded-full border-2 border-[#2d2a32] bg-white hover:bg-[#ff6b6b] hover:text-white flex items-center justify-center"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>

        {tab !== "forgot" && (
          <>
            <div className="text-[10px] tracking-[0.3em] text-[#5c5c5c] mb-1">THE COUPONER</div>
            <h2 className="serif text-3xl leading-none mb-1">
              {tab === "signin" ? "Welcome back." : "Join in."}
            </h2>
            <p className="text-sm text-[#5c5c5c] mb-5">
              {tab === "signin"
                ? "Sign in to save deals & vote."
                : "Free forever. Save deals across devices."}
            </p>

            {/* Social buttons */}
            <div className="grid gap-2 mb-4">
              <button
                onClick={startGoogleLogin}
                data-testid="login-google-modal-btn"
                className="h-11 rounded-full bg-[#D34836] hover:bg-[#b73824] text-white font-bold text-xs tracking-[0.2em] flex items-center justify-center gap-2 hard-shadow-sm"
              >
                <span className="w-5 h-5 rounded-full bg-white text-[#D34836] flex items-center justify-center font-black text-xs">G</span>
                CONTINUE WITH GOOGLE
              </button>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={notReady("Apple")}
                  data-testid="login-apple-btn"
                  className="h-11 rounded-full bg-[#2d2a32] hover:opacity-90 text-white font-bold text-xs tracking-[0.18em] flex items-center justify-center gap-2 hard-shadow-sm"
                >
                  <span className="text-lg leading-none"></span> APPLE
                </button>
                <button
                  onClick={notReady("Facebook")}
                  data-testid="login-facebook-btn"
                  className="h-11 rounded-full bg-[#1877F2] hover:bg-[#1462cf] text-white font-bold text-xs tracking-[0.18em] flex items-center justify-center gap-2 hard-shadow-sm"
                >
                  <span className="font-black">f</span> FACEBOOK
                </button>
              </div>
            </div>

            <div className="flex items-center gap-2 my-4">
              <div className="flex-1 h-px bg-[#2d2a32]/30" />
              <span className="text-[10px] tracking-[0.3em] text-[#5c5c5c]">OR EMAIL</span>
              <div className="flex-1 h-px bg-[#2d2a32]/30" />
            </div>
          </>
        )}

        {tab === "signin" && (
          <form onSubmit={handleSignIn} data-testid="signin-form" className="space-y-3">
            <LabeledInput icon={Mail} type="email" placeholder="you@email.com" value={email} onChange={setEmail} testid="signin-email" required />
            <LabeledInput icon={Lock} type="password" placeholder="Password" value={password} onChange={setPassword} testid="signin-password" required />
            {error && <p className="text-xs text-[#ff6b6b] font-bold" data-testid="signin-error">{error}</p>}
            <button
              type="submit"
              disabled={busy}
              data-testid="signin-submit"
              className="w-full h-11 rounded-full bg-[#2d2a32] text-white font-bold text-xs tracking-[0.2em] hover:bg-[#ff6b6b] hard-shadow-sm disabled:opacity-50"
            >
              {busy ? "SIGNING IN..." : "SIGN IN"}
            </button>
            <div className="flex items-center justify-between text-xs pt-1">
              <button type="button" onClick={() => { setTab("forgot"); setError(""); }} data-testid="show-forgot" className="text-[#5c5c5c] hover:text-[#ff6b6b] underline underline-offset-2">
                Forgot password?
              </button>
              <button type="button" onClick={() => { setTab("signup"); setError(""); }} data-testid="show-signup" className="font-bold">
                Sign up →
              </button>
            </div>
          </form>
        )}

        {tab === "signup" && (
          <form onSubmit={handleSignUp} data-testid="signup-form" className="space-y-3">
            <LabeledInput icon={UserIcon} type="text" placeholder="Your name (optional)" value={name} onChange={setName} testid="signup-name" />
            <LabeledInput icon={Mail} type="email" placeholder="you@email.com" value={email} onChange={setEmail} testid="signup-email" required />
            <LabeledInput icon={Lock} type="password" placeholder="Password (8+ characters)" value={password} onChange={setPassword} testid="signup-password" required minLength={8} />
            {error && <p className="text-xs text-[#ff6b6b] font-bold" data-testid="signup-error">{error}</p>}
            <button
              type="submit"
              disabled={busy}
              data-testid="signup-submit"
              className="w-full h-11 rounded-full bg-[#ff6b6b] text-white font-bold text-xs tracking-[0.2em] hover:bg-[#2d2a32] hard-shadow-sm disabled:opacity-50"
            >
              {busy ? "CREATING..." : "CREATE ACCOUNT"}
            </button>
            <div className="text-center text-xs text-[#5c5c5c] pt-1">
              Have one already?{" "}
              <button type="button" onClick={() => { setTab("signin"); setError(""); }} data-testid="show-signin" className="font-bold text-[#2d2a32] underline">
                Sign in
              </button>
            </div>
          </form>
        )}

        {tab === "forgot" && (
          <>
            <button
              onClick={() => { setTab("signin"); setError(""); }}
              data-testid="back-to-signin"
              className="text-[10px] tracking-[0.3em] text-[#5c5c5c] hover:text-[#ff6b6b] mb-3 flex items-center gap-1"
            >
              <ArrowLeft className="w-3 h-3" /> BACK
            </button>
            <h2 className="serif text-3xl leading-none mb-1">Forgot password?</h2>
            <p className="text-sm text-[#5c5c5c] mb-5">
              Enter your email — we&#39;ll send you a reset link that&#39;s good for 1 hour.
            </p>
            <form onSubmit={handleForgot} data-testid="forgot-form" className="space-y-3">
              <LabeledInput icon={Mail} type="email" placeholder="you@email.com" value={email} onChange={setEmail} testid="forgot-email" required />
              {error && <p className="text-xs text-[#ff6b6b] font-bold" data-testid="forgot-error">{error}</p>}
              <button
                type="submit"
                disabled={busy}
                data-testid="forgot-submit"
                className="w-full h-11 rounded-full bg-[#2d2a32] text-white font-bold text-xs tracking-[0.2em] hover:bg-[#ff6b6b] hard-shadow-sm disabled:opacity-50"
              >
                {busy ? "SENDING..." : "SEND RESET LINK"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

function LabeledInput({ icon: Icon, type, placeholder, value, onChange, testid, required, minLength }) {
  return (
    <div className="relative">
      <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5c5c5c]" />
      <input
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        minLength={minLength}
        data-testid={testid}
        className="w-full h-11 pl-10 pr-3 rounded-lg border-2 border-[#2d2a32] bg-white text-sm focus:outline-none focus:border-[#ff6b6b]"
      />
    </div>
  );
}
